import { useRouter } from "expo-router";
import { useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { Badge, Button, Card, Input, Screen, Text } from "~/components/ui";
import { useStarter } from "~/hooks/useStarter";
import { useAuth } from "~/lib/auth";
import { spacing, type ThemeColors } from "~/styles/tokens";
import { useTheme, useThemedStyles } from "~/styles/theme";
import { confirmAsync } from "~/utils/confirm";
import { notify } from "~/utils/notify";

function formatTimestamp(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function StarterScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const { user } = useAuth();
  const {
    starter,
    logs,
    loading,
    createStarter,
    feedStarter,
    deleteStarter,
    hoursUntilNextFeed,
  } = useStarter();
  const isAnonymous = !user;

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [name, setName] = useState("");
  const [feedInterval, setFeedInterval] = useState("12");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [feeding, setFeeding] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const hours = hoursUntilNextFeed();
  const isOverdue = hours !== null && hours < 0;

  if (loading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </Screen>
    );
  }

  const handleCreate = async () => {
    if (!name.trim()) {
      notify("Name required", "Give your starter a name!");
      return;
    }
    const intervalHours = parseInt(feedInterval, 10);
    if (
      Number.isNaN(intervalHours) ||
      intervalHours < 6 ||
      intervalHours > 72
    ) {
      notify(
        "Invalid interval",
        "Feeding interval must be between 6 and 72 hours.",
      );
      return;
    }
    setCreating(true);
    try {
      await createStarter(name.trim(), intervalHours, notes);
      setShowCreateForm(false);
    } catch (error: unknown) {
      notify(
        "Error",
        error instanceof Error ? error.message : "Could not create starter",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleFeed = async () => {
    setFeeding(true);
    try {
      await feedStarter();
      notify(
        "Fed! 🎉",
        `${starter!.name} has been fed. See you in ${starter!.feedingIntervalHours}h!`,
      );
    } catch (error: unknown) {
      notify(
        "Error",
        error instanceof Error ? error.message : "Could not log feeding",
      );
    } finally {
      setFeeding(false);
    }
  };

  const handleDelete = async () => {
    if (!starter) return;
    const confirmed = await confirmAsync({
      title: "Delete starter",
      message: `Delete ${starter.name} and its feeding log? This action cannot be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      await deleteStarter();
    } catch (error: unknown) {
      notify(
        "Error",
        error instanceof Error ? error.message : "Could not delete starter",
      );
    } finally {
      setDeleting(false);
    }
  };

  if (!starter && !showCreateForm) {
    return (
      <Screen>
        <View style={styles.center}>
          <Text style={styles.bigEmoji}>🫙</Text>
          <Text variant="heading" align="center">
            No starter yet
          </Text>
          <Text
            variant="body"
            align="center"
            color={colors.muted}
            style={styles.emptyCopy}
          >
            Set up your sourdough starter to begin tracking feedings.
          </Text>
          <Button
            label="Create starter"
            onPress={() => setShowCreateForm(true)}
            variant="primary"
            fullWidth
            style={styles.emptyPrimary}
          />
          <Button
            label="Set up notifications"
            onPress={() => router.push("/notifications")}
            variant="ghost"
            size="sm"
          />
        </View>
      </Screen>
    );
  }

  if (showCreateForm) {
    return (
      <Screen scroll>
        <Text variant="heading" style={styles.formTitle}>
          New starter
        </Text>
        <Card elevation="raised" style={styles.formCard}>
          <Input
            label="Starter name"
            placeholder="e.g. Bubbles, Sir Doughsalot…"
            value={name}
            onChangeText={setName}
            autoCapitalize="words"
          />
          <Input
            label="Feeding interval (hours)"
            placeholder="12"
            value={feedInterval}
            onChangeText={setFeedInterval}
            keyboardType="numeric"
            hint="How often do you feed it? Usually 12–24h"
          />
          <Input
            label="Notes (optional)"
            placeholder="Flour type, hydration level…"
            value={notes}
            onChangeText={setNotes}
            multiline
            numberOfLines={3}
          />
        </Card>
        <Button
          label="Save starter"
          onPress={() => void handleCreate()}
          loading={creating}
          variant="primary"
          fullWidth
          style={styles.formSave}
        />
        <Button
          label="Cancel"
          onPress={() => setShowCreateForm(false)}
          variant="ghost"
          fullWidth
          style={styles.formCancel}
        />
      </Screen>
    );
  }

  return (
    <Screen scroll>
      {isAnonymous ? (
        <Card elevation="flat" style={styles.bannerCard}>
          <Text variant="caption" color={colors.heading}>
            Guest starters stay on this device. Save an account to keep{" "}
            {starter!.name} when you switch phones.
          </Text>
          <Button
            label="Save account"
            onPress={() => router.push("/auth")}
            variant="secondary"
            size="sm"
            style={styles.bannerBtn}
          />
        </Card>
      ) : null}

      <View style={styles.starterHeader}>
        <Text style={styles.bigEmoji}>🫙</Text>
        <Text variant="heading" align="center">
          {starter!.name}
        </Text>
        {isOverdue ? (
          <Badge
            label={`Overdue by ${Math.abs(Math.round(hours!))}h`}
            variant="error"
            style={styles.headerBadge}
          />
        ) : hours !== null && hours < 4 ? (
          <Badge
            label={`Feed in ${Math.round(hours)}h`}
            variant="warning"
            style={styles.headerBadge}
          />
        ) : hours === null ? (
          <Badge
            label="Feed schedule unavailable"
            variant="default"
            style={styles.headerBadge}
          />
        ) : (
          <Badge
            label={`Next feed in ${Math.round(hours)}h`}
            variant="success"
            style={styles.headerBadge}
          />
        )}
      </View>

      <Card elevation="lifted" style={styles.feedCard}>
        <Text variant="label">Last fed</Text>
        <Text variant="title" style={styles.feedWhen}>
          {formatTimestamp(starter!.lastFedAt)}
        </Text>
        <Text variant="caption">
          Feeding every {starter!.feedingIntervalHours}h
        </Text>
        <Button
          label={feeding ? "Logging feed…" : "🥣 Feed now"}
          onPress={() => void handleFeed()}
          loading={feeding}
          variant="primary"
          fullWidth
          style={styles.feedBtn}
        />
      </Card>

      <Button
        label="Set up notifications"
        onPress={() => router.push("/notifications")}
        variant="secondary"
        fullWidth
        style={styles.secondaryAction}
      />
      <Button
        label={deleting ? "Deleting starter…" : "Delete starter"}
        onPress={handleDelete}
        loading={deleting}
        variant="ghost"
        fullWidth
        style={styles.deleteAction}
      />

      {logs.length > 0 ? (
        <View style={styles.logSection}>
          <Text variant="label" style={styles.logTitle}>
            Feeding log
          </Text>
          <Card elevation="raised" padding="xs">
            {logs.map((log, index) => (
              <View
                key={log.id}
                style={[
                  styles.logRow,
                  index < logs.length - 1 && styles.logRowBorder,
                ]}
              >
                <View style={styles.logDot} />
                <Text variant="body">{formatTimestamp(log.fedAt)}</Text>
              </View>
            ))}
          </Card>
        </View>
      ) : null}

      <View style={{ height: spacing.xxl }} />
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.xl,
      gap: spacing.sm,
    },
    bigEmoji: {
      fontSize: 56,
      lineHeight: 64,
      textAlign: "center",
    },
    emptyCopy: {
      marginTop: spacing.xs,
    },
    emptyPrimary: {
      marginTop: spacing.lg,
    },
    bannerCard: {
      marginTop: spacing.md,
      marginBottom: spacing.sm,
      borderColor: c.accent,
      gap: spacing.sm,
    },
    bannerBtn: {
      alignSelf: "flex-start",
    },
    formTitle: {
      marginTop: spacing.lg,
      marginBottom: spacing.md,
    },
    formCard: {
      gap: spacing.xs,
    },
    formSave: {
      marginTop: spacing.md,
    },
    formCancel: {
      marginTop: spacing.xs,
    },
    starterHeader: {
      alignItems: "center",
      paddingTop: spacing.xl,
      paddingBottom: spacing.lg,
      gap: spacing.sm,
    },
    headerBadge: {
      alignSelf: "center",
    },
    feedCard: {
      marginBottom: spacing.md,
      gap: spacing.xs,
    },
    feedWhen: {
      marginTop: spacing.xs,
    },
    feedBtn: {
      marginTop: spacing.md,
    },
    secondaryAction: {
      marginBottom: spacing.sm,
    },
    deleteAction: {
      marginBottom: spacing.md,
    },
    logSection: {
      marginTop: spacing.sm,
    },
    logTitle: {
      marginBottom: spacing.sm,
      marginLeft: spacing.xs,
    },
    logRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      paddingVertical: spacing.sm + 2,
      paddingHorizontal: spacing.sm,
    },
    logRowBorder: {
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    logDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: c.accent,
    },
  });
